import type { MonitoringBanner as Banner } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { api } from '../../lib/api';
import { useCan } from '../../lib/session';

/** How often admins' open tabs ask for incidents; the checks themselves run every 5 minutes. */
const REFRESH_MS = 60_000;

/**
 * In-app admin banner (PLAN §13.2): shown to admins on every screen while a system check keeps failing, until it
 * passes again. Links to Admin → Monitoring. Nobody else sees it.
 */
export function MonitoringBanner() {
  const admin = useCan('admin.manage');
  const q = useQuery({
    queryKey: ['monitoring', 'banner'],
    queryFn: () => api<Banner>('/admin/monitoring/banner'),
    enabled: admin,
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  const incidents = q.data?.incidents ?? [];
  if (!admin || incidents.length === 0) return null;
  const names = incidents.map(i => i.label).join(', ');
  return (
    // A labelled region rather than role="alert": it is present on every screen until the incident closes, not a
    // one-off announcement.
    <section
      aria-label="System checks"
      data-testid="monitoring-banner"
      className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-danger/40 bg-surface px-3 py-2 text-sm"
    >
      <TriangleAlert className="size-4 text-danger-foreground" aria-hidden="true" />
      <span>
        <span className="font-medium text-danger-foreground">
          {incidents.length === 1 ? 'A system check is failing' : `${incidents.length} system checks are failing`}:
        </span>{' '}
        {names}.
      </span>
      <Link to="/admin/monitoring" className="ms-auto font-medium underline">
        Open Monitoring
      </Link>
    </section>
  );
}
